import React, { useEffect, useState } from "react";
import { useAuth } from "../context/AuthContext";
import { Plus } from "lucide-react";

export default function AdminPartnersPage() {
    const { api } = useAuth();
    const [partners, setPartners] = useState([]);
    const [showForm, setShowForm] = useState(false);
    const [form, setForm] = useState({
        organization_name: "", primary_contact_name: "", primary_contact_email: "",
        billing_tier: "standard", billing_model: "per_workspace",
        workspace_cap: "", employee_cap: "", notes: "",
    });
    const [busy, setBusy] = useState(false);
    const [feedback, setFeedback] = useState(null);

    const refresh = () => api.get("/v2/admin/partners").then(r => setPartners(r.data.partners || [])).catch(() => {});
    useEffect(() => { refresh(); /* eslint-disable-next-line */ }, []);

    const provision = async () => {
        setBusy(true); setFeedback(null);
        try {
            const payload = { ...form };
            payload.workspace_cap = form.workspace_cap ? parseInt(form.workspace_cap) : null;
            payload.employee_cap = form.employee_cap ? parseInt(form.employee_cap) : null;
            const r = await api.post("/v2/admin/partners", payload);
            setFeedback({ ok: true, msg: `${r.data.organization_name} provisioned · ${r.data.primary_contact.email} · temp password ${r.data.primary_contact.temp_password}` });
            setShowForm(false); refresh();
            setForm({ organization_name: "", primary_contact_name: "", primary_contact_email: "", billing_tier: "standard", billing_model: "per_workspace", workspace_cap: "", employee_cap: "", notes: "" });
        } catch (e) {
            setFeedback({ ok: false, msg: e.response?.data?.detail || "Provision failed" });
        } finally { setBusy(false); }
    };

    return (
        <div data-testid="admin-partners-page">
            <div className="mb-8 flex items-baseline justify-between">
                <div>
                    <h1 className="text-3xl font-display font-bold text-white">Partners</h1>
                    <p className="text-sm text-gray-500 mt-1">Partner organizations that manage their own portfolio of workspaces.</p>
                </div>
                <button onClick={() => setShowForm(!showForm)}
                    className="bg-white text-black px-4 py-2 text-sm font-semibold hover:bg-gray-200 inline-flex items-center gap-2"
                    data-testid="provision-partner-btn">
                    <Plus className="w-4 h-4" /> Provision Partner
                </button>
            </div>

            {feedback && (
                <div className={`p-4 mb-6 border ${feedback.ok ? "border-emerald-500/30 text-emerald-300" : "border-red-500/30 text-red-300"} text-xs font-mono`}
                    data-testid="partner-feedback">
                    {feedback.msg}
                </div>
            )}

            {showForm && (
                <div className="bg-card border border-border/40 p-6 mb-6" data-testid="provision-partner-form">
                    <div className="grid grid-cols-2 gap-4">
                        <FormField label="Organization name *" value={form.organization_name} onChange={v => setForm({ ...form, organization_name: v })} testid="partner-org-name" />
                        <FormField label="Primary contact name *" value={form.primary_contact_name} onChange={v => setForm({ ...form, primary_contact_name: v })} testid="partner-contact-name" />
                        <FormField label="Primary contact email *" type="email" value={form.primary_contact_email} onChange={v => setForm({ ...form, primary_contact_email: v })} testid="partner-contact-email" />
                        <div>
                            <label className="text-xs text-gray-500 block mb-1">Billing tier</label>
                            <select className="w-full bg-muted border border-border/40 px-3 py-2 text-sm text-white"
                                value={form.billing_tier} onChange={e => setForm({ ...form, billing_tier: e.target.value })}>
                                {["standard", "growth", "enterprise"].map(t => <option key={t}>{t}</option>)}
                            </select>
                        </div>
                        <div>
                            <label className="text-xs text-gray-500 block mb-1">Billing model</label>
                            <select className="w-full bg-muted border border-border/40 px-3 py-2 text-sm text-white"
                                value={form.billing_model} onChange={e => setForm({ ...form, billing_model: e.target.value })}>
                                <option value="per_workspace">Per workspace</option>
                                <option value="per_employee">Per employee</option>
                                <option value="flat_retainer">Flat retainer</option>
                            </select>
                        </div>
                        <FormField label="Workspace cap" type="number" value={form.workspace_cap} onChange={v => setForm({ ...form, workspace_cap: v })} />
                        <FormField label="Employee cap" type="number" value={form.employee_cap} onChange={v => setForm({ ...form, employee_cap: v })} />
                        <div className="col-span-2">
                            <label className="text-xs text-gray-500 block mb-1">Notes</label>
                            <textarea className="w-full bg-muted border border-border/40 px-3 py-2 text-sm text-white" rows={2}
                                value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} />
                        </div>
                    </div>
                    <div className="flex gap-3 mt-5 pt-4 border-t border-border/30">
                        <button onClick={provision} disabled={busy || !form.organization_name || !form.primary_contact_email}
                            className="bg-white text-black px-4 py-2 text-sm font-semibold disabled:opacity-40"
                            data-testid="submit-partner-btn">
                            {busy ? "Provisioning…" : "Provision Partner"}
                        </button>
                        <button onClick={() => setShowForm(false)} className="text-gray-400 hover:text-white text-sm">Cancel</button>
                    </div>
                </div>
            )}

            <div className="bg-card border border-border/40">
                <div className="grid grid-cols-[2fr_1fr_1fr_100px_100px_140px_120px] px-5 py-3 border-b border-border/40 text-xs text-gray-600 uppercase tracking-wider">
                    <span>Organization</span><span>Tier</span><span>Model</span><span>Workspaces</span><span>Users</span><span>Status</span><span>Provisioned</span>
                </div>
                {partners.map(p => (
                    <div key={p.id} className="grid grid-cols-[2fr_1fr_1fr_100px_100px_140px_120px] px-5 py-3 border-b border-border/20 items-center"
                        data-testid={`partner-row-${p.id}`}>
                        <span className="text-sm text-white">{p.organization_name}</span>
                        <span className="text-xs text-gray-500 font-mono">{p.billing_tier}</span>
                        <span className="text-xs text-gray-500 font-mono">{p.billing_model}</span>
                        <span className="text-xs text-white font-mono">{p.workspace_count}{p.workspace_cap ? ` / ${p.workspace_cap}` : ""}</span>
                        <span className="text-xs text-white font-mono">{p.user_count}</span>
                        <span className={`text-xs font-mono uppercase tracking-wider ${p.status === "active" ? "text-emerald-300" : p.status === "suspended" ? "text-amber-300" : "text-gray-500"}`}>{p.status}</span>
                        <span className="text-xs text-gray-600 font-mono">{p.provisioned_at?.slice(0, 10)}</span>
                    </div>
                ))}
                {partners.length === 0 && (
                    <div className="px-5 py-10 text-xs text-gray-500 text-center">No partners provisioned yet.</div>
                )}
            </div>
        </div>
    );
}

function FormField({ label, value, onChange, type = "text", testid }) {
    return (
        <div>
            <label className="text-xs text-gray-500 block mb-1">{label}</label>
            <input type={type} className="w-full bg-muted border border-border/40 px-3 py-2 text-sm text-white"
                value={value} onChange={e => onChange(e.target.value)} data-testid={testid} />
        </div>
    );
}
